/**
 * Skip logic dla Content Engine entry points (generateArticle/Structure/Content)
 * + batch orchestrator.
 *
 * Czytamy node.match_status:
 *  - matched_keep            → skip (juz jest wpis na WP)
 *  - skip_manual             → skip (user swiadomie odznaczyl)
 *  - partial                 → skip (wymaga manual review)
 *  - matched_refurbish bez approval → skip (czeka na user)
 *  - matched_refurbish z approval   → NIE skip (generuj, pozniej publishQueueService
 *                                               refurbishuje zamiast createPost)
 *  - unchecked / unmatched / (null) → NIE skip (standardowy flow)
 */

import { supabase } from "@/integrations/supabase/client";
import { logPublic, logError } from "./contentLogger";

const PREFIX = "[SKIP]";

export interface SkipDecision {
  skip: boolean;
  reason?:
    | "matched_keep"
    | "skip_manual"
    | "partial_needs_review"
    | "refurbish_pending_approval";
}

export async function shouldSkipNodeGeneration(nodeId: string): Promise<SkipDecision> {
  const { data, error } = await supabase
    .from("thematic_nodes")
    .select("match_status, refurbish_approved_by_user")
    .eq("id", nodeId)
    .maybeSingle();

  if (error) {
    // Niepobranie node'a NIE moze zatrzymac pipeline — loguj i pozwol generowac
    logError(PREFIX, `cannot load node ${nodeId}: ${error.message}`);
    return { skip: false };
  }
  if (!data) return { skip: false };

  const row = data as { match_status: string | null; refurbish_approved_by_user: boolean | null };

  if (row.match_status === "matched_keep") {
    return { skip: true, reason: "matched_keep" };
  }
  if (row.match_status === "skip_manual") {
    return { skip: true, reason: "skip_manual" };
  }
  if (row.match_status === "partial") {
    return { skip: true, reason: "partial_needs_review" };
  }
  if (row.match_status === "matched_refurbish" && !row.refurbish_approved_by_user) {
    return { skip: true, reason: "refurbish_pending_approval" };
  }
  // unchecked / unmatched / matched_refurbish+approved → nie skipujemy
  return { skip: false };
}

/**
 * Zapisuje skip_reason do thematic_node_content.engine_debug
 * i ustawia engine_stage='skipped_existing'.
 */
export async function updateNodeSkipStatus(
  nodeId: string,
  reason: NonNullable<SkipDecision["reason"]>,
): Promise<void> {
  // Load existing engine_debug
  const { data } = await supabase
    .from("thematic_node_content")
    .select("engine_debug")
    .eq("node_id", nodeId)
    .maybeSingle();

  const prevDebug = ((data as { engine_debug: Record<string, unknown> } | null)?.engine_debug ||
    {}) as Record<string, unknown>;

  const newDebug = {
    ...prevDebug,
    skip_reason: reason,
    skipped_at: new Date().toISOString(),
  };

  // Check czy row juz istnieje — bezpieczniej niz onConflict (bo nie wiemy czy jest UNIQUE na node_id)
  const { data: existingRow } = await supabase
    .from("thematic_node_content")
    .select("id")
    .eq("node_id", nodeId)
    .maybeSingle();

  if (existingRow) {
    await supabase
      .from("thematic_node_content")
      .update({
        engine_stage: "skipped_existing",
        engine_debug: newDebug,
      })
      .eq("node_id", nodeId);
  } else {
    const { data: nodeRow } = await supabase
      .from("thematic_nodes")
      .select("user_id, map_id")
      .eq("id", nodeId)
      .maybeSingle();
    const nr = nodeRow as { user_id: string; map_id: string } | null;
    if (!nr) {
      logError(PREFIX, `Node ${nodeId} not found dla updateNodeSkipStatus`);
      return;
    }
    await supabase.from("thematic_node_content").insert({
      node_id: nodeId,
      user_id: nr.user_id,
      map_id: nr.map_id,
      engine_stage: "skipped_existing",
      engine_debug: newDebug,
    });
  }

  logPublic(PREFIX, `Node ${nodeId.slice(0, 8)} → skipped (${reason})`);
}
